import type {
  ReportPreviewOverviewSection,
  ReportPreviewProfile,
  ReportPreviewReport,
} from 'api/analytics-reports/types';
import ReportPreviewOverviewActivity from 'app/modules/analytics/modules/reports/components/reportPreview/overview/ReportPreviewOverviewActivity';
import ReportPreviewOverviewBestContent from 'app/modules/analytics/modules/reports/components/reportPreview/overview/ReportPreviewOverviewBestContent';
import ReportPreviewOverviewChannels from 'app/modules/analytics/modules/reports/components/reportPreview/overview/ReportPreviewOverviewChannels';
import ReportPreviewOverviewSummary from 'app/modules/analytics/modules/reports/components/reportPreview/overview/ReportPreviewOverviewSummary';
import type { ReportPreviewLayout } from 'app/modules/analytics/modules/reports/constants/reportPreviewPageShellClassName';
import {
  buildOverviewReportPreviewPages,
  type OverviewPreviewPageBlock,
} from 'app/modules/analytics/modules/reports/utils/buildOverviewReportPreviewPages';
import { resolveReportPreviewProfileHeaderMeta } from 'app/modules/analytics/modules/reports/utils/resolveReportPreviewProfileHeaderMeta';
import { useTranslation } from 'react-i18next';
import ViewReportPreviewPageShell from '../../ViewAnalyticsReportModal/ViewReportPreviewPageShell';

interface Props {
  profile: ReportPreviewProfile;
  report: ReportPreviewReport;
  overviewSection: ReportPreviewOverviewSection;
  startPageNumber: number;
  totalPages: number;
  layout?: ReportPreviewLayout;
  breakBeforeFirstPage?: boolean;
}

function renderOverviewBlock(
  block: OverviewPreviewPageBlock,
  overviewSection: ReportPreviewOverviewSection,
  isGroupSelected: boolean,
  isComparisonEnabled: boolean,
  profileMeta: {
    name?: string;
    logoSrc?: string;
    pageType?: number;
  },
) {
  switch (block) {
    case 'summary':
      return (
        <ReportPreviewOverviewSummary
          data={overviewSection.data}
          profileMeta={profileMeta}
          isComparisonEnabled={isComparisonEnabled}
        />
      );
    case 'channels':
      return isGroupSelected ? (
        <ReportPreviewOverviewChannels data={overviewSection.data} />
      ) : null;
    case 'best_content':
      return (
        <ReportPreviewOverviewBestContent
          data={overviewSection.data}
          isGroupSelected={isGroupSelected}
        />
      );
    case 'activity':
      return <ReportPreviewOverviewActivity data={overviewSection.data} />;
    default:
      return null;
  }
}

const ReportPreviewOverviewPage = ({
  profile,
  report,
  overviewSection,
  startPageNumber,
  totalPages,
  layout = 'modal',
  breakBeforeFirstPage = false,
}: Props) => {
  const { t } = useTranslation();
  const isGroupSelected = profile.type === 'general';
  const isComparisonEnabled = overviewSection.compare != null;
  const profileHeaderMeta = resolveReportPreviewProfileHeaderMeta(profile, t);
  const previewPages = buildOverviewReportPreviewPages(isGroupSelected);
  const since = report.period.from ?? '';
  const until = report.period.to ?? '';

  return (
    <>
      {previewPages.map((page, index) => (
        <ViewReportPreviewPageShell
          key={`${profile.id}-overview-page-${index}`}
          profileLabel={profileHeaderMeta.label}
          profileLogoSrc={profileHeaderMeta.logoSrc}
          profilePageType={profileHeaderMeta.pageType}
          reportName={report.name}
          since={since}
          until={until}
          pageNumber={startPageNumber + index}
          totalPages={totalPages}
          layout={layout}
          breakBeforeFirstPage={breakBeforeFirstPage}
        >
          <div className="tw-flex tw-flex-col tw-gap-4">
            {page.blocks.map((block) => {
              const content = renderOverviewBlock(
                block,
                overviewSection,
                isGroupSelected,
                isComparisonEnabled,
                {
                  name: profileHeaderMeta.label,
                  logoSrc: profileHeaderMeta.logoSrc,
                  pageType: profileHeaderMeta.pageType,
                },
              );

              if (!content) {
                return null;
              }

              return <div key={block}>{content}</div>;
            })}
          </div>
        </ViewReportPreviewPageShell>
      ))}
    </>
  );
};

export default ReportPreviewOverviewPage;
